import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useCreateProject, useMe, useProjects } from "@/api/hooks";
import { Dialog } from "@/components/Dialog";
import { ProjectLogo } from "@/components/ProjectLogo";
import { useToast } from "@/components/Toast";
import { Button, Checkbox, Chip, EmptyState, Field, Input, LoadError, PageHeader, PageSpinner, RoleChip, Textarea } from "@/components/ui";

export const PROJECT_KEY_RE = /^[A-Z][A-Z0-9]{1,9}$/;

export function ProjectsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const projects = useProjects(showArchived);
  const canCreate = me.role === "manager";

  return (
    <div className="flex flex-col gap-5 p-6">
      <PageHeader
        title={t("projects.title")}
        actions={
          canCreate && (
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              {t("projects.newProject")}
            </Button>
          )
        }
      />
      <Checkbox label={t("projects.showArchived")} checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />

      {projects.isPending ? (
        <PageSpinner />
      ) : projects.isError ? (
        <LoadError onRetry={() => void projects.refetch()} />
      ) : projects.data.length === 0 ? (
        <div className="card">
          <EmptyState
            title={t("projects.emptyTitle")}
            body={canCreate ? t("projects.emptyManager") : t("projects.emptyMember")}
            action={
              canCreate && (
                <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                  {t("projects.newProject")}
                </Button>
              )
            }
          />
        </div>
      ) : (
        <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4 p-0">
          {projects.data.map((p) => (
            <li key={p.id}>
              <Link
                to={`/p/${p.key}`}
                className="card flex h-full flex-col gap-3 p-4 text-text no-underline transition-colors hover:border-accent hover:text-text"
              >
                <div className="flex items-center gap-3">
                  <ProjectLogo project={p} size={40} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[16px] font-semibold">{p.name}</div>
                    <div className="font-mono text-xs text-muted">{p.key}</div>
                  </div>
                  {p.archived && <Chip tone="outline">{t("projects.archived")}</Chip>}
                </div>
                {p.description && <p className="m-0 line-clamp-2 text-sm text-text-2">{p.description}</p>}
                <div className="mt-auto flex flex-wrap items-center justify-between gap-2 text-sm text-muted">
                  <span>{t("projects.membersCount", { count: p.member_count })}</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="sr-only">{t("projects.yourRole")}:</span>
                    <RoleChip role={p.my_role} />
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <NewProjectDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function NewProjectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const create = useCreateProject();
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const keyValid = PROJECT_KEY_RE.test(key);

  const close = () => {
    setKey("");
    setName("");
    setDescription("");
    create.reset();
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      { key, name: name.trim(), description: description.trim() },
      {
        onSuccess: (p) => {
          toast.success(t("projects.created"));
          close();
          navigate(`/p/${p.key}`);
        },
        onError: (err) => toast.error(err),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={t("projects.newProject")}
      footer={
        <>
          <Button onClick={close}>{t("common.cancel")}</Button>
          <Button
            type="submit"
            form="new-project"
            variant="primary"
            loading={create.isPending}
            disabled={!keyValid || !name.trim()}
          >
            {t("common.create")}
          </Button>
        </>
      }
    >
      <form id="new-project" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-3">
          <Field label={t("projects.key")}>
            {(p) => (
              <Input
                {...p}
                value={key}
                onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))}
                className="font-mono uppercase"
                autoFocus
                required
                spellCheck={false}
                placeholder="PAY"
              />
            )}
          </Field>
          <Field label={t("projects.name")}>
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} />}
          </Field>
        </div>
        <p className="-mt-2 m-0 text-xs text-muted">{t("projects.keyHint")}</p>
        <Field label={t("projects.description")} optional>
          {(p) => <Textarea {...p} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />}
        </Field>
      </form>
    </Dialog>
  );
}
